import React, { useEffect, useState } from 'react';
import { photoAt, photoMode } from '../lib/storePhotos';

// tailwind.config.js colors: stage, stage-edge, stage-dark (kept in sync by hand; used in an inline gradient)
const STAGE = '#4A4757';
const STAGE_EDGE = '#2A2934';
const STAGE_DARK = '#1A1A20';
const GLOW = '#E6E2D2';        // tailwind `stage-glow`: cream softened toward neutral (the round-1 stage colour)
const GLOW_EDGE = '#7D7A82';

// alpha' = K * (3A - R - G - B): opaque pure white -> transparent, anything darker than ~R=G=B=248/255 stays opaque
// (printed whites inside a product are rarely that pure; studio backgrounds are 254-255), with a
// short soft ramp so JPEG noise and anti-aliased edges fade instead of fringing; already-transparent pixels (A = 0, e.g.
// the filter region around the image) stay transparent. RGB unchanged.
export const KNOCKOUT_ID = 'lc-knockout-white';
const K = 12;
export const KNOCKOUT_MATRIX = `1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  ${-K} ${-K} ${-K} ${3 * K} 0`;

// One hidden <svg> with the filter, added to <body> the first time a store photo renders.
function useKnockoutFilter() {
  useEffect(() => {
    if (typeof document === 'undefined' || document.getElementById(KNOCKOUT_ID)) return;
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', '0');
    svg.setAttribute('height', '0');
    svg.setAttribute('aria-hidden', 'true');
    svg.style.position = 'absolute';
    const filter = document.createElementNS(ns, 'filter');
    filter.setAttribute('id', KNOCKOUT_ID);
    filter.setAttribute('color-interpolation-filters', 'sRGB');
    const m = document.createElementNS(ns, 'feColorMatrix');
    m.setAttribute('type', 'matrix');
    m.setAttribute('values', KNOCKOUT_MATRIX);
    filter.appendChild(m);
    svg.appendChild(filter);
    document.body.appendChild(svg);
  }, []);
}

// WO-35 (founder: "the white background doesnt blend into our darker styling"): the one frame for store-listing
// photos (WO-34: hotlinked from the store's Shopify CDN, white backgrounds, never re-hosted, so they cannot be edited).
// Round 3 (2026-10-28, founder: "the white background ... still there"): the flat cream stage still read as a light
// box on the dark UI, and multiply darkens the product itself. Now the photo's near-white pixels are keyed out by an
// SVG colour-matrix filter (KNOCKOUT_ID; a render-time filter, so it works on hotlinked images: nothing reads the
// pixels back) and a soft "spotlight" shows through: light only behind the product (tailwind `stage`), falling off to
// the tile's own dark colour (`stage-dark`) before the edges, no border or box. The product's colours are untouched;
// white parts inside it show the light centre. Without SVG-filter support the photo shows as before on the spotlight.
// Skeleton until it loads; onError lets the caller fall back to its mockup / drawing.
// Only store photos use this: hosted card images, generated collages and mockups keep their own rendering.
//
// variant: 'tile' (grids: sealed, set, series, search), 'hero' (product page), 'card' (a card photo, card-shaped slot)
// credit: 'overlay' (small pill), 'none' (the caller prints the credit, e.g. under the hero)
const VARIANTS = {
  // pad: keeps the product inside the lit area; spot: the spotlight's size (ellipse radii in % of the slot)
  tile: { pad: 'p-4 sm:p-5', spot: '62% 60%', width: 400, srcSet: [400, 800], sizes: '(max-width: 640px) 50vw, 260px' },
  hero: { pad: 'p-6 sm:p-10', spot: '60% 58%', width: 900, srcSet: [600, 900, 1200], sizes: '(max-width: 1024px) 100vw, 420px' },
  card: { pad: 'p-3', spot: '72% 66%', width: 600, srcSet: [400, 600, 900], sizes: '(max-width: 768px) 60vw, 300px' },
};

// Spotlight: stage colour in the middle, a soft fall-off, the dark tile colour from ~80% of the ellipse outwards.
// Glow (white print / off-white backgrounds, multiplied): a light cream core large enough for the product, then the
// same fall-off to the tile colour, so the photo's background melts into it without a rectangle.
export const glow = (spot) => `radial-gradient(ellipse ${spot} at 50% 48%, ${GLOW} 0%, ${GLOW} 46%, ${GLOW_EDGE} 68%, ${STAGE_DARK} 92%)`;
export const spotlight = (spot) => `radial-gradient(ellipse ${spot} at 50% 48%, ${STAGE} 0%, ${STAGE} 38%, ${STAGE_EDGE} 58%, ${STAGE_DARK} 88%)`;

export default function StorePhoto({ url, alt = '', credit = null, variant = 'tile', creditMode = 'overlay',
                                     className = '', onError = null, testId = 'store-photo', hints = null }) {
  const [loaded, setLoaded] = useState(false);
  const v = VARIANTS[variant] || VARIANTS.tile;
  useKnockoutFilter();
  // WO-35 round 3b: WO-34's hints pick the mode (lib/storePhotos.js photoMode): keyout | glow | plain.
  const mode = photoMode(hints);
  const keyOut = mode === 'keyout';
  return (
    <div className={`relative w-full h-full ${className}`} data-testid={testId}>
      <div data-stage className={`relative isolate w-full h-full overflow-hidden flex items-center justify-center ${v.pad}`}
           style={{ backgroundColor: STAGE_DARK, backgroundImage: mode === 'glow' ? glow(v.spot) : spotlight(v.spot) }}>
        {!loaded && <div className="absolute inset-[18%] animate-pulse rounded-full bg-stage/10 blur-xl" aria-hidden="true" />}
        <img src={photoAt(url, v.width)}
             srcSet={v.srcSet.map((w) => `${photoAt(url, w)} ${w}w`).join(', ')}
             sizes={v.sizes}
             alt={alt || 'Product photo'} loading="lazy" decoding="async" referrerPolicy="no-referrer"
             onLoad={() => setLoaded(true)}
             onError={onError}
             style={keyOut ? { filter: `url(#${KNOCKOUT_ID})` } : undefined}
             data-keyout={keyOut ? 'white' : 'none'} data-mode={mode}
             className={`relative max-w-full max-h-full object-contain transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${
               mode === 'plain' ? 'w-auto h-auto rounded-md shadow-[0_6px_20px_rgba(0,0,0,0.45)] ring-1 ring-purple/20'
                 : mode === 'glow' ? 'w-full h-full mix-blend-multiply' : 'w-full h-full'}`} />
        {credit && creditMode === 'overlay' && (
          <span className="absolute bottom-1 right-1 max-w-[75%] truncate rounded bg-dark/80 px-1.5 py-0.5 text-[10px] leading-tight text-cream/80"
                data-testid="photo-credit">
            {credit}
          </span>
        )}
      </div>
    </div>
  );
}
